import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { Deal, LoadingId } from '../../../types';

import { styled, useTheme } from '@mui/material';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-blue.svg';
import { Typography } from '../../../components/Typography/Typography';
import { useLoadingBar } from '../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../utils/errorNotifier';
import { useUnarchiveDeal } from '../../../queries/hooks/useArchiveDeal';

type Props = {
  unarchiveDealOpen: boolean;
  toggleUnarchiveDeal: () => void;
  deal: Deal;
};

export function UnarchiveDealModal({ deal, unarchiveDealOpen, toggleUnarchiveDeal }: Props) {
  const { colors } = useTheme();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();

  const dealName = deal?.name;
  const { mutate: onUnarchiveDeal, isLoading: unarchiveInProgress } = useUnarchiveDeal();

  const handleUnarchiveDeal = async () => {
    startLoading(LoadingId.deleteDeal);
    onUnarchiveDeal(deal.id, {
      onSuccess: () => {
        pushSuccessToast({
          title: 'Deal is Unarchived',
          message: `Deal ${dealName} has been successfully unarchived.`,
        });
        toggleUnarchiveDeal();
      },
      onError: (error: unknown) => {
        pushErrorToast({ message: extractErrorMessage(error) });
      },
      onSettled: () => {
        stopLoading(LoadingId.deleteDeal);
      },
    });
  };

  return (
    <ModalComponent
      isOpen={unarchiveDealOpen}
      onClose={toggleUnarchiveDeal}
      actionButtonsProps={{
        isSubmitDisabled: unarchiveInProgress,
        onSubmit: handleUnarchiveDeal,
        onCancel: toggleUnarchiveDeal,
        submitText: 'Unarchive Deal',
      }}
      title={`Unarchive ${dealName} deal`}
      top='80px'
    >
      <InfoWrapper>
        <InfoIcon />
        <Typography variant='body' color={colors.textAccent.default}>
          {`Are you sure you want to unarchive deal ${dealName}? It will become an active deal again. Its deal owners are preserved, but you will need to reassign its operating partners.`}
        </Typography>
      </InfoWrapper>
    </ModalComponent>
  );
}

const InfoWrapper = styled('div')`
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.highlighted};
  border-radius: 2px;
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;
